import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, ClockOutline, HStack, Link, rem, Text } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { useTrialStatus } from '../../../hooks/use-trial-status';

const OFFICE_HOURS_URL = 'https://calendly.com/bonusly-expert-office-hours';

// Same opacity timing as nav labels (IconAndTextContent).
const EXPANDED_TRANSITION = 'opacity 0.15s 0.075s linear, max-height 0.2s cubic-bezier(.4, 0, .2, 1), margin 0.2s cubic-bezier(.4, 0, .2, 1)';
const COLLAPSED_TRANSITION = 'opacity 0.15s cubic-bezier(.4, 0, .2, 1), max-height 0.2s cubic-bezier(.4, 0, .2, 1), margin 0.2s cubic-bezier(.4, 0, .2, 1)';
// Expanded sidebar content column: 260 - 8px padding on each side.
const EXPANDED_CONTENT_WIDTH_PX = 244;

interface TrialCardProps {
	isExpanded: boolean;
}

export const TrialCard: FC<TrialCardProps> = ({ isExpanded }) => {
	const keyPrefix = 'trial_card';
	const { t } = useTranslation('navigation', { keyPrefix });
	const company = useCurrentCompany();
	const { track } = useAnalytics();

	const { daysLeft, isPostExpiry, isInScope, canSeeWhatsNext } = useTrialStatus();

	// Stay mounted when collapsed so the opacity fade can run.
	if (!canSeeWhatsNext || !isInScope) return null;

	const count = daysLeft ?? 0;

	return (
		<Box
			overflow="hidden"
			maxH={isExpanded ? rem(240) : 0}
			opacity={isExpanded ? 1 : 0}
			my={isExpanded ? 2 : 0}
			width="100%"
			pointerEvents={isExpanded ? 'auto' : 'none'}
			aria-hidden={!isExpanded}
			transition={isExpanded ? EXPANDED_TRANSITION : COLLAPSED_TRANSITION}
			{...(!isExpanded && { inert: '' })}
		>
			{/* minW prevents reflow during the width tween; overflow clips. */}
			<Box bgColor="bg.surface" borderRadius="md" p={3} minW={rem(EXPANDED_CONTENT_WIDTH_PX)}>
				<Text color="text.default" fontSize="sm" fontWeight="semibold">
					{isPostExpiry ? t('expired_title') : t('title', { plan: company.product_plan_name })}
				</Text>

				<HStack mt={1} spacing={1}>
					<ClockOutline color="text.default" flexShrink={0} size="xs" />
					<Text color="text.default" fontSize="sm">
						{isPostExpiry ? t('expired_subtitle') : t('days', { count })}
					</Text>
				</HStack>

				<Link
					as={RouterLink}
					color="text.interactive"
					display="block"
					fontSize="sm"
					fontWeight="medium"
					to="/trial/whats-next"
					mt={2}
					onClick={() => track(AnalyticsEvent.OnboardingTrialBanner.ManagePlanClicked)}
					_hover={{ textDecor: 'none' }}
				>
					{isPostExpiry ? t('choose_plan') : t('manage_plan')}
				</Link>

				<Box mt={2} pt={2} borderTop="1px solid" borderTopColor="border.default">
					<Text color="text.softer" fontSize="sm">
						{t('need_help')}{' '}
						<Link href={OFFICE_HOURS_URL} target="_blank" _hover={{ textDecor: 'none' }}>
							{t('talk_to_expert')}
						</Link>
					</Text>
				</Box>
			</Box>
		</Box>
	);
};
